import { useCallback, useEffect, useState } from "react";
import { getSessionData } from "@repo/shared-utils/common-utils";
import { selectIsAuthSuccessOrPerpetual, useAppSelector } from "@repo/global-store";
import { Reminder } from "../../types/upcoming-live.types";
import { logReminderError, logReminderInfo, maskMobileNumber } from "../../utils/reminderLogger";
import { ReminderService } from "./UpcomingLive";

export function useReminders(reminderService?: ReminderService, influencerId?: string) {
  const isLoggedIn = useAppSelector(selectIsAuthSuccessOrPerpetual);
  const [reminders, setReminders] = useState<Map<string, Reminder>>(new Map());

  const fetchAllReminders = useCallback(async () => {
    if (!reminderService) {
      logReminderInfo("useReminders", "fetchAllReminders:skipped:no-reminder-service", {
        isLoggedIn,
      });
      return;
    }

    try {
      logReminderInfo("useReminders", "fetchAllReminders:start", {
        isLoggedIn,
        hasReminderService: Boolean(reminderService),
      });

      const sessionData = await getSessionData();
      const mobileNo = sessionData?.mobileNo;

      logReminderInfo("useReminders", "fetchAllReminders:session-data", {
        isLoggedIn,
        hasMobileNo: Boolean(mobileNo),
        mobileNoMasked: maskMobileNumber(mobileNo),
      });

      if (!mobileNo) {
        logReminderInfo("useReminders", "fetchAllReminders:skipped:no-mobile", {
          isLoggedIn,
        });
        return;
      }

      const response = await reminderService.getUserReminders({ mobileNo, influencerId });
      logReminderInfo("useReminders", "fetchAllReminders:response", {
        mobileNoMasked: maskMobileNumber(mobileNo),
        influencerId,
        hasResponseData: Boolean(response?.data),
        reminderCount: response?.data?.reminders?.length ?? 0,
        response,
      });

      if (response?.data?.reminders) {
        const remindersMap = new Map<string, Reminder>();
        response.data.reminders.forEach((reminder) => {
          if (reminder.isActive && reminder.videoId) {
            remindersMap.set(reminder.videoId, reminder);
          }
        });

        logReminderInfo("useReminders", "fetchAllReminders:success", {
          isLoggedIn,
          remindersCount: response.data.reminders.length,
          activeRemindersCount: remindersMap.size,
          activeReminderVideoIds: Array.from(remindersMap.keys()),
        });
        setReminders(remindersMap);
      }
    } catch (err) {
      logReminderError("useReminders", "fetchAllReminders:error", err, {
        isLoggedIn,
        influencerId,
      });
    }
  }, [influencerId, isLoggedIn, reminderService]);

  useEffect(() => {
    logReminderInfo("useReminders", "effect:auth-selector", {
      isLoggedIn,
      hasReminderService: Boolean(reminderService),
      shouldFetchReminders: Boolean(isLoggedIn && reminderService),
    });

    if (isLoggedIn && reminderService) {
      fetchAllReminders();
    }
  }, [isLoggedIn, fetchAllReminders, reminderService]);

  const handleReminderToggle = useCallback(
    async (videoId: string, isSet: boolean) => {
      logReminderInfo("useReminders", "handleReminderToggle:start", {
        videoId,
        isSet,
        isLoggedIn,
        hasReminderService: Boolean(reminderService),
      });

      if (!isSet) {
        setReminders((prev) => {
          const newMap = new Map(prev);
          newMap.delete(videoId);
          return newMap;
        });

        logReminderInfo("useReminders", "handleReminderToggle:local-state-updated", {
          videoId,
          action: "removed-from-local-map",
        });
      }

      if (isLoggedIn && reminderService) {
        await fetchAllReminders();
      }

      logReminderInfo("useReminders", "handleReminderToggle:completed", {
        videoId,
        isSet,
      });
    },
    [isLoggedIn, fetchAllReminders, reminderService]
  );

  return { reminders, handleReminderToggle };
}
